export type SurvivorRole = 'Scavenger' | 'Medic' | 'Cook' | 'Trader'

export type SurvivorStatus = 'Idle' | 'OnMission' | 'Injured' | 'Resting'

/** UI locale */
export type LanguageCode = 'nl' | 'en'

/** Bilingual copy for items, locations, events, UI-facing survivor bios, etc. */
export type LocalizedText = { nl: string; en: string }

export type UiTheme = 'dark' | 'light'

export type QuestId =
  | 'starter_survival'
  | 'den_bosch_gateway'
  | 'vehicle_quest'
  | 'cooking_skill_quest'
  | 'hideout_move_quest'
  | 'utrecht_gateway'

export type StarterSurvivalQuestState =
  | { status: 'inactive' }
  | { status: 'active'; phaseIndex: number }
  | { status: 'completed'; completedAt: number; acknowledged: boolean }

/** Opens Den Bosch in Trips after Rural South is fully explored; manual start; loot objectives unlock first city trip */
export type DenBoschGatewayQuestState =
  | { status: 'inactive' }
  | { status: 'available' }
  | {
      status: 'active'
      abandonedFarmTripsCompleted: number
      foodLootValueAccumulated: number
      waterLootValueAccumulated: number
    }
  | { status: 'completed'; completedAt: number; acknowledged: boolean }

/** Introduces the garage and vehicle mechanic */
export type VehicleQuestState =
  | { status: 'inactive' }
  | { status: 'active'; phaseIndex: number }
  | { status: 'completed'; completedAt: number; acknowledged: boolean }

/** Teaches book-based skill learning and kitchen assignment; activates on 3rd survivor */
export type CookingSkillQuestState =
  | { status: 'inactive' }
  | { status: 'active'; phaseIndex: number }
  | { status: 'completed'; completedAt: number; acknowledged: boolean }

/** Guides the player through the move hideout feature; activates after den_bosch_gateway completes */
export type HideoutMoveQuestState =
  | { status: 'inactive' }
  | { status: 'active'; phaseIndex: number }
  | { status: 'completed'; completedAt: number; acknowledged: boolean }

/** Unlocks A2 corridor then Utrecht after Den Bosch is fully explored; Option B radio + broadcast + recruits + stockpile */
export type UtrechtGatewayQuestState =
  | { status: 'inactive' }
  | { status: 'available' }
  | {
      status: 'active'
      startedAt: number
      /** 0–3: find broken radio → repair → build broadcast → post-broadcast goals */
      phaseIndex: number
      survivorsAtStart: number
      /** Recruitment accepts (expedition + broadcast) excluding Trader role */
      nonTraderRecruitsSinceStart: number
      scrapLootValuePostBroadcast: number
      electronicsLootValuePostBroadcast: number
    }
  | { status: 'completed'; completedAt: number; acknowledged: boolean }

export type QuestRuntimeState = {
  starterSurvival: StarterSurvivalQuestState
  denBoschGateway: DenBoschGatewayQuestState
  vehicleQuest: VehicleQuestState
  cookingSkillQuest: CookingSkillQuestState
  hideoutMoveQuest: HideoutMoveQuestState
  utrechtGateway: UtrechtGatewayQuestState
}

export type GameSettings = {
  language: LanguageCode
  notificationsEnabled: boolean
  soundEnabled: boolean
  theme: UiTheme
}

export const DEFAULT_GAME_SETTINGS: GameSettings = {
  language: 'en',
  notificationsEnabled: false,
  soundEnabled: false,
  theme: 'dark',
}

// ── Vehicles ───────────────────────────────────────────────────────────
export type VehicleSize = 'small' | 'medium' | 'large'

export type VehicleDef = {
  id: string
  name: LocalizedText
  blurb: LocalizedText
  size: VehicleSize
  /** Multiplier applied to all item quantities found on expedition (stacks with needs mult.) */
  lootMultiplier: number
  /** Passengers the vehicle can take on an expedition (team size cap when this vehicle is selected). */
  passengerSeats: number
  audioKind: 'bike' | 'car'
  glyph: string
  /**
   * Vehicle parts that can be upgraded on this vehicle instance.
   * Each vehicle has 3 parts, upgradable to level 3 (1→2→3).
   */
  parts: VehiclePartDef[]
}

export type VehicleInstance = {
  id: string
  defId: string
  /** 0–100; vehicle becomes unusable when durability hits 0 */
  durability: number
  /**
   * Per-part upgrade levels for this vehicle instance.
   * Missing keys should be treated as level 1.
   */
  partLevels: Record<string, 1 | 2 | 3>
}

export type VehiclePartUpgradeEffects = Partial<{
  /** Multiplier applied to `VehicleDef.lootMultiplier`. */
  lootMultiplierFactor: number
  /** Seats added to `VehicleDef.passengerSeats`. */
  passengerSeatBonus: number
  /** Multiplier applied to trip duration (1 = no change; <1 reduces duration). */
  durationMultiplierFactor: number
  /**
   * Multiplier applied to durability loss per trip (1 = baseline; <1 reduces wear).
   * Applied on top of the rolled durability loss range.
   */
  durabilityLossMultiplierFactor: number
}>

export type VehiclePartUpgrade = {
  /** Target level after the upgrade (2 or 3). */
  toLevel: 2 | 3
  costs: RecipeIngredient[]
  effects: VehiclePartUpgradeEffects
}

export type VehiclePartDef = {
  id: string
  name: LocalizedText
  blurb: LocalizedText
  /** Simple icon for the UI; glyphs are short strings. */
  glyph?: string
  upgrades: Partial<Record<2 | 3, VehiclePartUpgrade>>
}

// ── Items ──────────────────────────────────────────────────────────────
export type ItemCategory = 'food' | 'water' | 'meds' | 'scrap' | 'trinket' | 'weapon'
export type ItemRarity = 'common' | 'uncommon' | 'special'

export type WeaponFamily = 'bow' | 'melee'

export type ItemDef = {
  id: string
  name: LocalizedText
  blurb: LocalizedText
  category: ItemCategory
  rarity: ItemRarity
  /**
   * Generic value field. For trinkets this is monetary worth. For other
   * categories it isn't currently used for restoration math (workstations
   * have fixed restore amounts), but reserved for future tuning.
   */
  value: number
  /** Optional emoji/shorthand icon for lists (craft preview, stash rows, etc.). */
  glyph?: string
  /** Present when this stash item is an equippable weapon */
  weapon?: {
    family: WeaponFamily
    dmg: number
  }
}

export type ItemStack = {
  defId: string
  quantity: number
  locked?: boolean
}

export type Needs = {
  hunger: number
  thirst: number
  energy: number
}

export type SkillKey =
  | 'stealth'
  | 'guns'
  | 'melee'
  | 'heavy_weapons'
  | 'explosives'
  | 'archery'
  | 'cybernetics'
  | 'looting'
  | 'first_aid'
  | 'survival'
  | 'endurance'
  | 'animal_training'
  | 'navigation'
  | 'fishing'
  | 'climbing'
  | 'parkour'
  | 'swimming'
  | 'lockpicking'
  | 'programming'
  | 'engineering'
  | 'electronics'
  | 'tinkering'
  | 'bartering'
  | 'persuasion'
  | 'linguistics'
  | 'charisma'
  | 'construction'
  | 'crafting'
  | 'cooking'
  | 'agriculture'
  | 'gaming'
  | 'art'
  | 'music'
  | 'driving'

export type SurvivorSkillState = {
  level: number
  xp: number
  unlocked: boolean
}

export type Skills = Partial<Record<SkillKey, SurvivorSkillState>>

export type EquipmentSlot = 'weapon' | 'tool'

export type Equipment = {
  id: string
  name: LocalizedText
  slot: EquipmentSlot
  blurb: LocalizedText
  /** When this weapon was equipped from `ITEMS` stash definition */
  weaponDefId?: string
  weaponFamily?: WeaponFamily
  /** Base damage from item definition (before skill multiplier) */
  baseDamage?: number
}

export type Loadout = Partial<Record<EquipmentSlot, Equipment | null>>

export type SurvivorGender = 'male' | 'female' | 'nonbinary' | 'unspecified'

export type Survivor = {
  id: string
  name: string
  role: SurvivorRole
  status: SurvivorStatus
  /** Epoch ms when injury auto-heals; null/undefined means not on timed injury. */
  injuryEndsAt?: number | null
  needs: Needs
  skills: Skills
  loadout: Loadout
  bio: LocalizedText
  gender?: SurvivorGender
  portrait?: string
}

// ── Locations ──────────────────────────────────────────────────────────
export type RegionId = 'rural-south' | 'den-bosch' | 'a2-highway' | 'utrecht'

export type LocationId =
  | 'vinkel-bridge'
  | 'rural-supermarket'
  | 'cafe-pannekoek'
  | 'abandoned-farm'
  | 'verkade-factory'
  | 'town-hall-offices'
  | 'fashion-stores'
  | 'old-market-hall'
  | 'a2-highway-road'
  | 'a2-rest-stop-collapsed'
  | 'utrecht-domplein'
  | 'utrecht-oudegracht'
  | 'utrecht-centraal'
  | 'utrecht-jaarbeurs'
  | 'utrecht-galgenwaard'
  | 'utrecht-umc-fringe'
  | 'utrecht-lombok'
  | 'utrecht-hoog-catharijne'
  | 'utrecht-nieuwegracht'

export type LocationUnlockRequirement = {
  locationId: LocationId
  explorationPct: number
}

export type ExplorationTierReward = {
  thresholdPct: number
  guaranteedLoot: ItemStack[]
}

export type LocationExplorationState = {
  progressPct: number
  claimedRewardThresholds: number[]
  recruitOffered: boolean
}

/**
 * Loot table entry for a location. References an ItemDef by id.
 * Weight is relative; rolls per expedition produce one item each.
 */
export type LootWeight = {
  itemId: string
  weight: number
  min: number
  max: number
}

export type Location = {
  id: LocationId
  region: RegionId
  name: LocalizedText
  blurb: LocalizedText
  durationMs: number
  injuryLevel: {
    minDurationMs: number
    maxDurationMs: number
  }
  cost: Needs
  loot: LootWeight[]
  rolls: { min: number; max: number }
  diary: LocalizedText[]
  unlockRequirement?: LocationUnlockRequirement
  exploration: {
    progressGain: { min: number; max: number }
    lootRevealAtPct: number
    tierRewards: ExplorationTierReward[]
    recruit?: {
      atPct: number
      survivor: Survivor
    }
  }
}

export type Region = {
  id: RegionId
  name: LocalizedText
  blurb: LocalizedText
  unlockAt: number // expeditions completed before this region opens
}

/** Per-survivor outcome before aggregate fields on TripReport. */
export type TripParticipantOutcome = {
  survivorId: string
  needsBefore: Needs
  needsAfter: Needs
  injured: boolean
}

export type Expedition = {
  id: string
  locationId: LocationId
  /** Team members in dispatch order; leader is `survivorIds[0]` (same as `survivorId`). */
  survivorIds: string[]
  /** @deprecated Alias for `survivorIds[0]` — kept for sync (`survivor_id`) and legacy saves. */
  survivorId: string
  startedAt: number
  endsAt: number
  status: 'active' | 'returned'
  returnedAt?: number
  report?: TripReport
  /** Server-set dedupe for push notifications (synced when present). */
  notifiedAt?: number
  /** Three pre-rolled diary lines for the trip; revealed with progress. */
  diary: string[]
  /** VehicleInstance.id of the vehicle used on this trip (if any). */
  vehicleInstanceId?: string
  /** VehicleDef.id of the vehicle, kept alongside instanceId for loot calc. */
  vehicleDefId?: string
  /** All vehicle instance IDs when multiple vehicles are used on a trip. */
  vehicleInstanceIds?: string[]
  /** All vehicle def IDs when multiple vehicles are used on a trip. */
  vehicleDefIds?: string[]
}

export type TripReport = {
  locationId: LocationId
  survivorId: string
  collectedItems: ItemStack[]
  explorationBeforePct?: number
  explorationAfterPct?: number
  explorationDeltaPct?: number
  explorationRewards?: ItemStack[]
  unlockedLocationIds?: LocationId[]
  /** Leader / aggregate snapshot; detail per member is in `participantOutcomes`. */
  needsBefore: Needs
  needsAfter: Needs
  /** True if any participant was injured. */
  injured: boolean
  participantOutcomes?: TripParticipantOutcome[]
  foundSurvivor?: Survivor
  /** Vehicle found on this trip that can be stored in the garage. */
  foundVehicleDefId?: string
}

// Loot delivered when an expedition resolves.
export type LootDelivery = ItemStack[]

export type Toast = {
  id: string
  survivorName: string
  message?: LocalizedText
  /**
   * Stacks granted (positive) or lost (negative) from the resolved expedition,
   * including any event modifier. Negative values appear when an event
   * effect deducts from current inventory.
   */
  delivered: ItemStack[]
  removed?: ItemStack[]
}

export type WorkstationId =
  | 'base_station'
  | 'stash'
  | 'kitchen'
  | 'well'
  | 'infirmary'
  | 'lodging'
  | 'broadcast_station'
  | 'workshop'
  | 'garage'
  | 'training_yard'
  | 'computer_lab'
  | 'market_stall'

export type WorkstationKind =
  | 'restoreNeed'
  /** Treat timed injuries only; consumes meds category */
  | 'injuryCare'
  /** Houses — roster cap + survivors rest here for energy regen */
  | 'lodging'
  /** Radio recruit queue (communication center) */
  | 'broadcast'
  | 'craft'
  | 'locked'
  /** Mission hub — narrative / link to expeditions */
  | 'hub'
  /** Stash capacity / storage narrative */
  | 'storage'
  /** Vehicle storage and maintenance */
  | 'garage'
  /** Passive XP / timed training sessions */
  | 'training'
  /** Slot-based terminals — hacking yields */
  | 'computer_lab'
  /** Trader timed missions — spend trinketPoints */
  | 'market'

export type WorkstationDef = {
  id: WorkstationId
  name: LocalizedText
  /**
   * Short line above `name` on hub cards — one label per upgrade tier (levels 1–3).
   * E.g. kitchen: campfire → grill → full kitchen setup.
   */
  cardSurtitles: readonly [LocalizedText, LocalizedText, LocalizedText]
  glyph: string
  blurb: LocalizedText
  kind: WorkstationKind
  // For restoreNeed
  need?: keyof Needs
  /** Inventory category the workstation consumes one unit of */
  category?: ItemCategory
  needRestore?: number
  buttonLabel?: LocalizedText
  emptyLabel?: LocalizedText
}

/** Vinkel forest → workplace → Den Bosch train depot → Utrecht hub */
export type HideoutTierId =
  | 'forest_hideout'
  | 'boarded_workplace'
  | 'train_depot'
  | 'utrecht_centrum'

/** Computer salvage keys that can fill `computer_lab` slots */
export type ComputerPartDefId =
  | 'computer-tower-salvage'
  | 'laptop-salvage'
  | 'raspberry-pi-salvage'
  | 'monitor-salvage'

export type WorkstationRuntimeState = {
  built: boolean
  level: number
  assignedSurvivorId: string | null
  installedToolIds: string[]
  /** Items produced by passive generation and ready to claim into stash. */
  generatedItems: ItemStack[]
  /** Epoch ms anchor used to resolve offline generation cycles. */
  generationAnchorAt: number | null
  /** Epoch ms anchor used to resolve offline passive need regeneration. */
  needRegenAnchorAt: number | null
  /** Epoch ms when the gem group-heal becomes available again. */
  gemHealAvailableAt: number | null
  /** Epoch ms anchor used to resolve offline garage auto-repair cycles. */
  garageRepairAnchorAt: number | null
  /** Survivors currently resting at lodging (recover energy over time). */
  lodgingOccupantIds: string[]
  /** Pending radio recruits at broadcast station (not persisted items). */
  broadcastRecruitOffers: Survivor[]
  /** Four slots: installed computer-part def ids */
  computerLabSlots?: [string | null, string | null, string | null, string | null]
  /** Parallel assignees per slot */
  computerLabWorkers?: [string | null, string | null, string | null, string | null]
  /** Epoch ms when each slot job completes */
  computerLabJobEndsAt?: [
    number | null,
    number | null,
    number | null,
    number | null,
  ]
  /** Trader outbound mission */
  tradingMissionEndsAt?: number | null
  tradingMissionSurvivorId?: string | null
  /** Training yard — timed endurance session (uses `assignedSurvivorId`) */
  trainingYardSessionEndsAt?: number | null
}

export type PetSpecies = 'dog' | 'cat' | 'bird' | 'parrot'

export type PetInstance = {
  id: string
  species: PetSpecies
  bond: number
  name?: string
}

export type WorkstationUpgradeTierGate = {
  /** Minimum hideout tier before this upgrade can be applied */
  minHideoutTier: HideoutTierId
  costs: Array<
    | { defId: string; quantity: number }
    | { category: ItemCategory; quantity: number }
  >
  benefit: LocalizedText
}

/** Upgrade from (level) → level + 1 */
export type WorkstationUpgradePath = Partial<
  Record<number, WorkstationUpgradeTierGate>
>

export type WorkstationToolEffects = {
  /** Added to hunger/thirst/energy restore at restoreNeed stations */
  needRestoreBonus?: number
  /** Multiplier on craft duration (e.g. 0.85 = 15% faster) */
  craftDurationMultiplier?: number
  /** Added to garage auto-repair durability gain per minute while assigned. */
  vehicleRepairPerMinuteBonus?: number
}

export type WorkstationToolDef = {
  id: string
  workstationId: WorkstationId
  name: LocalizedText
  blurb: LocalizedText
  glyph?: string
  /** Minimum station level before this tool can be installed */
  requiredStationLevel: number
  costs: Array<
    | { defId: string; quantity: number }
    | { category: ItemCategory; quantity: number }
  >
  effects: WorkstationToolEffects
}

export type ShelterHideoutPersisted = {
  tierId: HideoutTierId
  workstations: Partial<Record<WorkstationId, WorkstationRuntimeState>>
}

export type OnboardingStep =
  | 'not_started'
  | 'character_create'
  | 'intro'
  /** Legacy steps — kept for saves / migration */
  | 'survivor_intro_1'
  | 'survivor_intro_2'
  | 'survivor_intro_3'
  | 'first_expedition'
  | 'first_return'
  | 'workstation_hint'
  | 'done'

export type TutorialStepId =
  | 'hideout_resources'
  | 'hideout_workstations'
  | 'nav_tabs'
  | 'survivors_list'
  | 'stash_items'
  | 'expeditions_locations'
  | 'quests_quest'
  | 'quests_milestones'

export type TutorialStep = TutorialStepId | 'done'

export type RecipeIngredient =
  | { defId: string; quantity: number }
  | { category: ItemCategory; quantity: number }

export type RecipeOutput =
  | {
      type: 'item'
      defId: string
      quantity: number
    }
  | {
      type: 'needs'
      needsDelta: Partial<Needs>
      energyBonus?: number
    }

export type RecipeDef = {
  id: string
  workstationId: WorkstationId
  name: LocalizedText
  blurb: LocalizedText
  glyph: string
  inputs: RecipeIngredient[]
  output: RecipeOutput
  durationMs: number
  /** Number of completed expeditions (`progress`) before recipe appears */
  unlockAt: number
  /** Minimum upgraded workstation level (default 1) */
  minWorkstationLevel?: number
  /** XP granted to the workstation assignee when the craft completes */
  skillXp?: { skill: SkillKey; amount: number }
}

export type CraftJob = {
  id: string
  recipeId: string
  workstationId: WorkstationId
  startedAt: number
  endsAt: number
  /** Assignee when the job started (XP targets this survivor even if reassigned mid-craft). */
  assignedSurvivorId?: string | null
}

export type AchievementId =
  | 'first_100_food_collected'
  | 'first_den_bosch_trip'
  | 'first_5_crafts'
  | 'out_the_door'
  | 'strength_in_numbers'
  | 'full_house'
  | 'on_four_wheels'
  | 'new_home'
  | 'signal_found'
  | 'water_bearer'
  | 'workshop_regular'
  | 'street_wise'
  | 'blooded'

export type ProgressStats = {
  totalFoodCollected: number
  totalCraftsCompleted: number
  denBoschTripsCompleted: number
  utrechtTripsCompleted: number
  totalExpeditionsCompleted: number
  survivorsRecruited: number
  vehiclesFound: number
  hideoutMoves: number
  radioBuilt: boolean
  totalWaterCollected: number
  totalInjuries: number
}
